import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Stack, TextField, Typography } from '@mui/material';
import { Button } from 'components/index';
import { Close } from '@mui/icons-material';

const MAX_CHANGE_ORDER_ROWS = 50;

interface AddChangeOrdersDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (count: number) => void;
  loading?: boolean;
  // Names the rows will get, so the user can see what's about to be added before confirming.
  previewDescriptions: (count: number) => string[];
}

const AddChangeOrdersDialog: React.FC<AddChangeOrdersDialogProps> = ({ open, onClose, onConfirm, loading, previewDescriptions }) => {
  // Kept as a string so the field can be emptied while typing without snapping back to 1.
  const [countDraft, setCountDraft] = React.useState('1');

  React.useEffect(() => {
    setCountDraft('1');
  }, [open]);

  const parsedCount = parseInt(countDraft, 10);
  const isValid = Number.isFinite(parsedCount) && parsedCount >= 1 && parsedCount <= MAX_CHANGE_ORDER_ROWS;
  const preview = isValid ? previewDescriptions(parsedCount) : [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='xs'>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        Add Change Orders
        <IconButton onClick={onClose} sx={{ marginLeft: 'auto' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label='How many change order rows?'
            type='number'
            value={countDraft}
            onChange={event => setCountDraft(event.target.value)}
            inputProps={{ min: 1, max: MAX_CHANGE_ORDER_ROWS }}
            error={countDraft !== '' && !isValid}
            helperText={countDraft !== '' && !isValid ? `Enter a number between 1 and ${MAX_CHANGE_ORDER_ROWS}` : ' '}
            autoFocus
            fullWidth
          />
          {preview.length > 0 && (
            <Typography variant='body2' color='text.secondary'>
              Will add: {preview.join(', ')} — each marked as a change order with empty amounts you can fill in like any other row.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant='outlined' color='error'>
          Cancel
        </Button>
        <Button onClick={() => onConfirm(parsedCount)} disabled={!isValid} variant='contained' loading={loading}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddChangeOrdersDialog;
